import React from 'react';
import BuildingAppearanceFields from '../BuildingAppearanceFields';
import type { Service } from '../../../../../../shared/types/config';

export interface BuildingTypeConfigItem {
  id: number;
  code: string;
  service_id: number;
  nom: string;
  icon: string | null;
  couleur: string | null;
  ordre: number;
  prix: number;
  actif: number;
  created_at?: string;
}

interface BatimentsTabProps {
  t: (key: string) => string;
  loading: boolean;
  services: Service[];
  buildingForm: {
    code: string;
    nom: string;
    icon: string;
    couleur: string;
    ordre: number;
    actif: number;
    service_id: number;
    prix: number;
  };
  setBuildingForm: React.Dispatch<React.SetStateAction<{
    code: string;
    nom: string;
    icon: string;
    couleur: string;
    ordre: number;
    actif: number;
    service_id: number;
    prix: number;
  }>>;
  buildingTypesConfig: BuildingTypeConfigItem[];
  editingBuildingId: number | null;
  savingBuilding: boolean;
  handleSaveBuilding: () => void;
  handleCancelEditBuilding: () => void;
  handleEditBuilding: (b: BuildingTypeConfigItem) => void;
  handleDeleteBuilding: (id: number) => void;
}

/** Onglet « Bâtiments » extrait de SystemConfiguration.tsx. */
const BatimentsTab: React.FC<BatimentsTabProps> = ({
  t,
  loading,
  services,
  buildingForm,
  setBuildingForm,
  buildingTypesConfig,
  editingBuildingId,
  savingBuilding,
  handleSaveBuilding,
  handleCancelEditBuilding,
  handleEditBuilding,
  handleDeleteBuilding,
}) => (
                  <div className="space-y-6">
                    <p className="text-sm text-gray-400">
                      {t('batiments.description')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.codeLabel')}</label>
                        <input
                          type="text"
                          value={buildingForm.code}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, code: e.target.value }))}
                          placeholder={t('batiments.codePlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                          disabled={!!editingBuildingId}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.nomLabel')}</label>
                        <input
                          type="text"
                          value={buildingForm.nom}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, nom: e.target.value }))}
                          placeholder={t('batiments.nomPlaceholder')}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.serviceLabel')}</label>
                        <select
                          value={buildingForm.service_id}
                          onChange={(e) =>
                            setBuildingForm((f) => ({ ...f, service_id: parseInt(e.target.value, 10) || 1 }))
                          }
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        >
                          {services.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.nom}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">{t('batiments.serviceHint')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.prixLabel')}</label>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={Number.isFinite(buildingForm.prix) ? buildingForm.prix : 0}
                          onChange={(e) =>
                            setBuildingForm((f) => ({
                              ...f,
                              prix: Math.max(0, parseFloat(e.target.value) || 0),
                            }))
                          }
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <BuildingAppearanceFields
                        icon={buildingForm.icon}
                        couleur={buildingForm.couleur}
                        onIconChange={(icon) => setBuildingForm((f) => ({ ...f, icon }))}
                        onCouleurChange={(couleur) => setBuildingForm((f) => ({ ...f, couleur }))}
                        labels={{
                          iconLabel: t('batiments.iconLabel'),
                          iconPlaceholder: t('batiments.iconPlaceholder'),
                          iconPickHint: t('batiments.iconPickHint'),
                          couleurLabel: t('batiments.couleurLabel'),
                          couleurPlaceholder: t('batiments.couleurPlaceholder'),
                          colorPickHint: t('batiments.colorPickHint'),
                          customTailwind: t('batiments.customTailwind'),
                        }}
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">{t('batiments.ordreLabel')}</label>
                        <input
                          type="number"
                          min={0}
                          value={buildingForm.ordre}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, ordre: parseInt(e.target.value, 10) || 0 }))}
                          className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="building-actif"
                          checked={!!buildingForm.actif}
                          onChange={(e) => setBuildingForm((f) => ({ ...f, actif: e.target.checked ? 1 : 0 }))}
                          className="rounded border-gray-600 bg-gray-800 text-primary-500"
                        />
                        <label htmlFor="building-actif" className="text-sm text-gray-300">{t('batiments.actifLabel')}</label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSaveBuilding}
                        disabled={savingBuilding}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-60"
                      >
                        {savingBuilding ? t('batiments.saving') : editingBuildingId ? t('batiments.update') : t('batiments.add')}
                      </button>
                      {editingBuildingId && (
                        <button
                          type="button"
                          onClick={handleCancelEditBuilding}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
                        >
                          {t('batiments.cancel')}
                        </button>
                      )}
                    </div>
                    <div className="border-t border-gray-700 pt-4">
                      <h2 className="text-lg font-semibold text-white mb-3">{t('batiments.listTitle')}</h2>
                      {loading ? (
                        <p className="text-sm text-gray-500">{t('batiments.loading')}</p>
                      ) : buildingTypesConfig.length === 0 ? (
                        <p className="text-sm text-gray-500">{t('batiments.empty')}</p>
                      ) : (
                        <ul className="divide-y divide-gray-800">
                          {buildingTypesConfig.map((b) => (
                            <li key={b.id} className="py-2 flex items-center justify-between gap-4">
                              <span className="text-2xl">{b.icon || '🏢'}</span>
                              <span
                                className={`flex-1 ${
                                  b.actif ? b.couleur || 'text-gray-200' : 'text-gray-500'
                                }`}
                              >
                                {b.nom} <span className="text-gray-500">({b.code})</span>
                                {!b.actif && <span className="ml-2 text-xs text-amber-400">{t('batiments.inactif')}</span>}
                              </span>
                              <span className="text-xs text-gray-500">
                                {t('batiments.ordre')}: {b.ordre} · {t('batiments.prixShort')}{' '}
                                {typeof b.prix === 'number' ? b.prix.toFixed(2) : Number(b.prix || 0).toFixed(2)} €
                              </span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditBuilding(b)}
                                  className="px-2 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded"
                                >
                                  {t('batiments.modify')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteBuilding(b.id)}
                                  className="px-2 py-1 text-sm bg-red-600/80 hover:bg-red-600 text-white rounded"
                                >
                                  {t('batiments.delete')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
);

export default BatimentsTab;
